import { Facebook, Instagram, Place, WhatsApp } from '@mui/icons-material';
import { AppBar, Box, IconButton, SvgIconProps, Toolbar, Tooltip, Typography, useTheme } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranding } from '../../contexts/BrandingContext';
import { whatsAppLink } from '../../library/models/tenantBranding';
import ResourcesIds from '../../resources/ResourcesIds';
import { footerTextStyles, footerToolbarStyles, getFooterStyles, socialBoxStyles, socialIconStyles } from './footer.styles';

/**
 * The platform's footer, on every page including the ones a signed-out visitor sees.
 *
 * The contact icons are the clinic's, never the platform's. They used to be Motion Care's
 * WhatsApp number, Instagram, Facebook and map pin, hardcoded -- which under one static
 * build serving every clinic meant telling each clinic's patients how to reach a
 * competitor. They come from `GET /tenant-branding` now, which is the call the app already
 * makes on boot and caches per tenant, so the icons are on screen from the first frame of a
 * reload rather than a round trip later.
 *
 * **Nothing is drawn until a session names a clinic.** A signed-out visitor and the platform
 * owner -- who belongs to none -- both get the copyright line alone, because there is no
 * clinic to be contacted about. A clinic that has set one of the four gets one icon, not
 * three broken ones.
 *
 * **The copyright names the clinic, not the platform.** It was hardcoded to `appName`, which
 * put "© Carefull" at the foot of every clinic's own front door -- the last line on
 * motioncare.ro telling Motion Care's patients whose product they are really looking at. It
 * survived the first pass because the landing page's own test renders the page without the
 * layout around it, so nothing rendered this. It is the same mistake as falling back to the
 * platform's mark in the header, one component further down.
 */
export const Footer = (): JSX.Element => {
    const theme = useTheme();
    const { t: translate } = useTranslation();
    // logoAlt, not appName: it is already "the clinic's name when known, otherwise the
    // platform's", which is exactly what a copyright line needs.
    const { branding, logoAlt: owner } = useBranding();

    // Icons are wrapped rather than referenced, the same way About's card icons are: the
    // exports are OverridableComponents, which do not fit a plain function type.
    const links: SocialLink[] = [
        {
            key: 'instagram',
            href: branding?.instagramUrl ?? null,
            Icon: (props: SvgIconProps) => <Instagram {...props} />,
            label: translate(ResourcesIds.FOOTER_INSTAGRAM),
        },
        {
            key: 'facebook',
            href: branding?.facebookUrl ?? null,
            Icon: (props: SvgIconProps) => <Facebook {...props} />,
            label: translate(ResourcesIds.FOOTER_FACEBOOK),
        },
        {
            key: 'whatsapp',
            href: whatsAppLink(branding?.whatsappNumber),
            Icon: (props: SvgIconProps) => <WhatsApp {...props} />,
            label: translate(ResourcesIds.FOOTER_WHATSAPP),
        },
        {
            key: 'maps',
            href: branding?.mapsUrl ?? null,
            Icon: (props: SvgIconProps) => <Place {...props} />,
            label: translate(ResourcesIds.FOOTER_MAPS),
        },
    ].filter((link): link is SocialLink => link.href !== null);

    return (
        <AppBar
            position="static"
            component="footer"
            color="default"
            sx={getFooterStyles(theme)}
        >
            <Toolbar sx={footerToolbarStyles}>
                <Typography variant="body2" sx={footerTextStyles}>
                    © {new Date().getFullYear()} {owner}
                </Typography>

                {links.length > 0 && (
                    <Box sx={socialBoxStyles}>
                        {links.map((link: SocialLink) => (
                            <Tooltip key={link.key} title={link.label}>
                                <IconButton
                                    component="a"
                                    href={link.href}
                                    target="_blank"
                                    // noreferrer alongside noopener: the clinic's own map
                                    // pin is harmless, but these are values an admin typed
                                    // and the app never has to say where a visitor came from.
                                    rel="noopener noreferrer"
                                    aria-label={link.label}
                                    size="small"
                                    sx={socialIconStyles}
                                >
                                    <link.Icon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ))}
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
};

/** One configured way to reach the clinic. `href` is null until it is filtered out. */
interface SocialLink {
    key: string;
    href: string;
    Icon: (props: SvgIconProps) => JSX.Element;
    label: string;
}
